import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Send, Pencil } from "lucide-react";
import api from "../lib/api";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import PageShell from "../components/PageShell";

const labelsByRole = {
  therapist: [
    ["session_overview", "Session Overview"],
    ["key_themes", "Key Themes"],
    ["client_progress_observations", "Client Progress and Observations"],
    ["goals_next_steps", "Goals and Next Steps"],
  ],
  coach: [
    ["session_overview", "Session Overview"],
    ["key_wins_insights", "Key Wins and Insights"],
    ["action_items", "Action Items"],
    ["focus_next_session", "Focus for Next Session"],
  ],
};

const ReviewSummary = () => {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [sections, setSections] = useState({});
  const [notesContent, setNotesContent] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [attachedIds, setAttachedIds] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/sessions/${sessionId}`).then((r) => {
      setData(r.data);
      setSections(r.data.sections || {});
      setNotesContent(r.data.notes_content || "");
    }).catch(() => nav("/dashboard"));
  }, [sessionId, nav]);

  if (!data) return <div className="p-8 text-center text-[#4A5D52]">Loading…</div>;

  const labels = labelsByRole[data.user_role] || labelsByRole.therapist;
  const isAI = data.source_type === "ai_listen";

  const updateSection = (key, val) => setSections((prev) => ({ ...prev, [key]: val }));

  const toggleFeedback = (id) => setAttachedIds((m) => ({ ...m, [id]: !m[id] }));

  const send = async () => {
    setSending(true);
    setError("");
    try {
      // Save edits first
      if (isAI) {
        await api.put(`/sessions/${sessionId}`, { sections });
      } else {
        await api.put(`/sessions/${sessionId}`, { notes_content: notesContent });
      }
      await api.post(`/sessions/${sessionId}/send`, { attached_feedback_ids: [] });
      nav(`/results/${sessionId}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-40 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-wider text-[#8BA38E] uppercase">
            {isAI ? "Review Summary" : "Review Notes"}
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[#2A3B32]">{data.client_name}</h1>
          <p className="mt-1 text-xs text-[#8A9C91] flex items-center justify-center gap-1.5">
            <Pencil size={12} /> Edit anything below before sending
          </p>
        </div>

        {isAI ? (
          labels.map(([key, label]) => (
            <GlassCard key={key} data-testid={`section-edit-${key}`}>
              <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">{label}</h3>
              <textarea
                value={sections[key] || ""}
                onChange={(e) => updateSection(key, e.target.value)}
                rows={Math.max(3, Math.min(10, (sections[key] || "").split("\n").length + 2))}
                data-testid={`section-input-${key}`}
                className="mt-3 w-full bg-white/40 border border-white/50 text-[#2A3B32] rounded-[16px] p-3 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] resize-none leading-relaxed text-base"
              />
            </GlassCard>
          ))
        ) : (
          <GlassCard data-testid="notes-edit-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Session Notes</h3>
            <textarea
              value={notesContent}
              onChange={(e) => setNotesContent(e.target.value)}
              rows={14}
              data-testid="notes-edit-textarea"
              className="mt-3 w-full bg-white/40 border border-white/50 text-[#2A3B32] rounded-[16px] p-3 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] resize-none leading-relaxed text-base"
            />
          </GlassCard>
        )}

        {feedback.length > 0 && false && (
          <GlassCard data-testid="feedback-attach-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">
              Client Feedback for {data.client_name}
            </h3>
            <p className="mt-2 text-xs text-[#8A9C91]">Tap to attach to this session note.</p>
            <div className="mt-4 flex flex-col gap-3">
              {feedback.map((fb) => {
                const id = fb.feedback_id || fb.entry_id;
                const checked = !!attachedIds[id];
                return (
                  <button
                    key={id}
                    onClick={() => toggleFeedback(id)}
                    data-testid={`feedback-toggle-${id}`}
                    className={`text-left p-4 rounded-[16px] border transition-all ${
                      checked
                        ? "bg-[#8BA38E]/15 border-[#8BA38E]/50"
                        : "bg-white/40 border-white/40 hover:bg-white/55"
                    }`}
                  >
                    <p className="text-sm text-[#2A3B32] whitespace-pre-wrap">{fb.content}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-[#8A9C91]">
                        {new Date(fb.created_at).toLocaleDateString()}
                      </p>
                      <span className={`text-xs font-medium ${checked ? "text-[#6A7F6C]" : "text-[#8A9C91]"}`}>
                        {checked ? "Attached" : "Tap to attach"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </GlassCard>
        )}

        {isAI && data.transcript && (
          <GlassCard>
            <button
              onClick={() => setShowTranscript((v) => !v)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-transcript-btn"
            >
              <span className="font-semibold text-[#2A3B32]">Full transcript</span>
              <ChevronDown
                size={20}
                className={`text-[#4A5D52] transition-transform ${showTranscript ? "rotate-180" : ""}`}
              />
            </button>
            {showTranscript && (
              <p
                className="mt-4 text-sm text-[#4A5D52] whitespace-pre-wrap leading-relaxed"
                data-testid="transcript-content"
              >
                {data.transcript}
              </p>
            )}
          </GlassCard>
        )}

        {error && <p className="text-center text-sm text-[#B85C5C]" data-testid="review-error">{error}</p>}

        <div className="flex flex-col gap-3 max-w-md mx-auto w-full">
          <PrimaryButton onClick={send} disabled={sending} data-testid="review-send-btn">
            <Send size={18} className="mr-2" />
            {sending ? "Sending…" : "Send"}
          </PrimaryButton>
          <GlassButton onClick={() => nav("/dashboard")} data-testid="review-cancel-btn">Save Draft & Exit</GlassButton>
        </div>
      </div>
    </PageShell>
  );
};

export default ReviewSummary;
